/**
 * V169 — Phase 3 Agents Dashboard Tab
 *
 * Single tab that surfaces all 5 autonomous business agents:
 *   1. Accounting   — 30-day revenue / expenses / net profit
 *   2. Returns Triage — LLM-categorised customer return requests
 *   3. Shipping     — stuck-package detection
 *   4. Tax / VAT    — quarterly EU OSS summary
 *   5. Chargeback   — open Stripe disputes
 *
 * Each agent has an "Activate" CTA when inactive; once active, it streams
 * its snapshot data into a compact card.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import {
  Calculator, RefreshCcw, Truck, Receipt, ShieldAlert,
  Sparkles, Loader2, ArrowUpRight, ArrowDownRight, TrendingUp,
  Globe2, Bell, Zap, ChevronDown, CheckCircle, Send, Mail,
} from "lucide-react";
import { API } from "../../config";

const AGENT_META = [
  { key: "accounting", title: "Accounting", icon: Calculator, color: "emerald", desc: "Denný P&L: výnosy, náklady, čistý zisk." },
  { key: "returns",    title: "Returns Triage", icon: RefreshCcw, color: "amber", desc: "AI triedi vrátenia tovaru a navrhuje odpovede zákazníkovi." },
  { key: "shipping",   title: "Shipping", icon: Truck, color: "blue", desc: "Detekuje uviaznuté balíky a sleduje výkonnosť kuriérov." },
  { key: "tax",        title: "Tax / VAT", icon: Receipt, color: "violet", desc: "Štvrťročné OSS hlásenie pre cross-border B2C predaje v EU." },
  { key: "chargeback", title: "Chargeback", icon: ShieldAlert, color: "rose", desc: "Sleduje Stripe disputes a AI píše návrh evidence." },
];

export default function Phase3AgentsTab({ user, t = {} }) {
  const email = (user?.email || "").toLowerCase();
  const [snapshots, setSnapshots] = useState({});
  const [loading, setLoading] = useState(true);
  const [activating, setActivating] = useState(null);
  const [activatingAll, setActivatingAll] = useState(false);
  const [activateAllResult, setActivateAllResult] = useState(null);

  const load = async () => {
    if (!email) { setLoading(false); return; }
    try {
      const r = await axios.get(`${API}/phase3/all-snapshots`, { params: { email } });
      setSnapshots(r.data || {});
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [email]);

  const activate = async (agent) => {
    setActivating(agent);
    try {
      await axios.post(`${API}/phase3/${agent}/start`, { email });
      await load();
    } catch (e) {
      console.error(e);
    } finally {
      setActivating(null);
    }
  };

  const activateAll = async () => {
    setActivatingAll(true);
    setActivateAllResult(null);
    try {
      const r = await axios.post(`${API}/phase3/activate-all`, { email, inherit_webhook: true });
      setActivateAllResult(r.data);
      await load();
      setTimeout(() => setActivateAllResult(null), 6000);
    } catch (e) {
      console.error(e);
    } finally {
      setActivatingAll(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-3 text-gray-500 p-6" data-testid="phase3-loading">
        <Loader2 className="w-5 h-5 animate-spin" />
        <span>{t.p3_loading || "Načítavam Phase 3 agentov…"}</span>
      </div>
    );
  }

  const allActive = ["accounting", "returns", "shipping", "tax", "chargeback"]
    .every((k) => snapshots[k]?.active);

  return (
    <div className="space-y-5" data-testid="phase3-tab">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-xl font-black text-gray-800 font-['Chivo'] flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-violet-600" />
            {t.p3_title || "Phase 3 Agenti"}
          </h2>
          <p className="text-xs text-gray-500 mt-1">
            {t.p3_subtitle || "Päť autonómnych agentov pre váš biznis: účtovníctvo, reklamácie, doprava, DPH a chargebacks."}
          </p>
        </div>
        {!allActive && (
          <button
            onClick={activateAll}
            disabled={activatingAll}
            className="bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-700 hover:to-fuchsia-700 text-white text-sm font-bold px-4 py-2.5 rounded-lg disabled:opacity-60 flex items-center gap-2 shadow-md hover:shadow-lg transition-all"
            data-testid="p3-activate-all"
          >
            {activatingAll ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
            {activatingAll ? (t.p3_activating_all || "Aktivujem…") : (t.p3_activate_all || "Aktivovať všetkých 5 agentov")}
          </button>
        )}
      </div>

      {/* Activate-all toast */}
      {activateAllResult && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 flex items-start gap-3" data-testid="p3-activate-all-toast">
          <CheckCircle className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
          <div className="flex-1 text-sm">
            <p className="font-bold text-emerald-900">
              {t.p3_activated_count || "Aktivovaných"}: {activateAllResult.activated?.length || 0}/5 {t.p3_agents_word || "agentov"}
            </p>
            {activateAllResult.webhook_inherited && (
              <p className="text-emerald-700 text-xs mt-1">
                ✨ {t.p3_webhook_inherited || "Slack webhook zdedený z Inventory Agenta"}: <code className="bg-emerald-100 px-1 rounded">{activateAllResult.webhook_url_masked}</code>
              </p>
            )}
          </div>
        </div>
      )}

      {/* Daily Brief panel */}
      <DailyBriefPanel email={email} t={t} />

      <div className="grid md:grid-cols-2 gap-4">
        {AGENT_META.map((m) => {
          const snap = snapshots[m.key] || {};
          const active = snap.active;
          return (
            <div key={m.key}
                 className="bg-white border border-gray-200 rounded-lg overflow-hidden"
                 data-testid={`p3-card-${m.key}`}>
              <div className={`px-4 py-3 border-b border-gray-100 flex items-center gap-3 bg-${m.color}-50`}>
                <m.icon className={`w-5 h-5 text-${m.color}-600`} />
                <div className="flex-1 min-w-0">
                  <h3 className="font-bold text-gray-800 truncate">{m.title}</h3>
                  <p className="text-[10px] text-gray-500 truncate">{m.desc}</p>
                </div>
                <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${
                  active ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-500"
                }`}>{active ? "Active" : "Inactive"}</span>
              </div>

              <div className="p-4">
                {!active ? (
                  <button
                    onClick={() => activate(m.key)}
                    disabled={activating === m.key}
                    className={`w-full bg-${m.color}-600 hover:bg-${m.color}-700 text-white text-sm font-bold py-2 rounded disabled:opacity-60 flex items-center justify-center gap-2`}
                    data-testid={`p3-activate-${m.key}`}>
                    {activating === m.key ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                    {t.p3_activate || "Aktivovať"}
                  </button>
                ) : (
                  <>
                    <AgentBody agent={m.key} snap={snap} t={t} reload={load} />
                    <WebhookPanel agent={m.key} email={email} snap={snap}
                                  color={m.color} t={t} onChange={load} />
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function AgentBody({ agent, snap, t, reload }) {
  if (agent === "accounting") return <AccountingBody snap={snap} t={t} />;
  if (agent === "returns")    return <ReturnsBody snap={snap} t={t} reload={reload} />;
  if (agent === "shipping")   return <ShippingBody snap={snap} t={t} />;
  if (agent === "tax")        return <TaxBody snap={snap} t={t} />;
  if (agent === "chargeback") return <ChargebackBody snap={snap} t={t} />;
  return null;
}

function StatRow({ label, value, hint, trend }) {
  return (
    <div className="flex items-center justify-between py-1">
      <span className="text-xs text-gray-500">{label}</span>
      <span className="text-sm font-bold text-gray-800 flex items-center gap-1">
        {trend === "up" && <ArrowUpRight className="w-3 h-3 text-emerald-600" />}
        {trend === "down" && <ArrowDownRight className="w-3 h-3 text-rose-600" />}
        {value} {hint && <span className="text-[10px] text-gray-400 font-normal">{hint}</span>}
      </span>
    </div>
  );
}

function AccountingBody({ snap, t }) {
  const tot = snap.totals || {};
  const trend = tot.delta_vs_prev_pct > 0 ? "up" : tot.delta_vs_prev_pct < 0 ? "down" : null;
  return (
    <div className="space-y-1" data-testid="p3-accounting-body">
      <StatRow label={t.p3_revenue_30d || "Výnosy 30d"} value={`€${tot.revenue_30d || 0}`} trend={trend} hint={`${tot.delta_vs_prev_pct || 0}%`} />
      <StatRow label={t.p3_expenses_30d || "Náklady 30d"} value={`€${tot.expenses_30d || 0}`} />
      <StatRow label={t.p3_net_30d || "Čistý zisk 30d"} value={`€${tot.net_profit_30d || 0}`} />
      {snap.hint && <p className="text-[10px] text-amber-700 bg-amber-50 px-2 py-1 rounded mt-2">{snap.hint}</p>}
    </div>
  );
}

function ReturnsBody({ snap, t, reload }) {
  const tot = snap.totals || {};
  const cats = snap.by_category || {};
  const email = (snap.agent?.email || "").toLowerCase();
  const [sendingId, setSendingId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState("");
  const [statusMsg, setStatusMsg] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkPreview, setBulkPreview] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  const openBulk = async () => {
    setBulkBusy(true);
    setBulkResult(null);
    try {
      const r = await axios.get(`${API}/phase3/returns/bulk-reply-preview`, {
        params: { email },
      });
      setBulkPreview(r.data);
      setBulkOpen(true);
    } catch (e) {
      setStatusMsg({ id: "bulk", text: `✗ ${e?.response?.data?.detail || "error"}`, err: true });
      setTimeout(() => setStatusMsg(null), 3500);
    } finally {
      setBulkBusy(false);
    }
  };

  const runBulk = async () => {
    setBulkBusy(true);
    try {
      const r = await axios.post(`${API}/phase3/returns/bulk-reply`, { email });
      setBulkResult(r.data);
      if (reload) await reload();
    } catch (e) {
      setBulkResult({ failed: 1, sent: 0, total: 0,
                      error: e?.response?.data?.detail || "error" });
    } finally {
      setBulkBusy(false);
    }
  };

  const closeBulk = () => {
    setBulkOpen(false);
    setBulkPreview(null);
    setBulkResult(null);
  };

  const send = async (ret, override) => {
    setSendingId(ret.return_id);
    setStatusMsg(null);
    try {
      const body = override !== undefined ? { custom_message: override } : {};
      const r = await axios.post(`${API}/phase3/returns/${ret.return_id}/send-reply`, body);
      setStatusMsg({ id: ret.return_id, text: `${t.p3_ret_sent || "Odoslané"} → ${r.data.sent_to}` });
      setEditingId(null);
      setDraft("");
      if (reload) await reload();
    } catch (e) {
      setStatusMsg({ id: ret.return_id, text: `✗ ${e?.response?.data?.detail || "error"}`, err: true });
    } finally {
      setSendingId(null);
      setTimeout(() => setStatusMsg(null), 4000);
    }
  };

  return (
    <div className="space-y-1" data-testid="p3-returns-body">
      <StatRow label={t.p3_returns_open || "Otvorené"} value={tot.open || 0} />
      <StatRow label={t.p3_returns_resolved || "Vyriešené"} value={tot.resolved || 0} />
      <StatRow label={t.p3_returns_total || "Celkom 30d"} value={tot.total_30d || 0} />

      {(tot.open || 0) >= 2 && (
        <button
          onClick={openBulk}
          disabled={bulkBusy}
          className="mt-2 w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white text-xs font-bold px-3 py-2 rounded disabled:opacity-60 flex items-center justify-center gap-2"
          data-testid="p3-ret-bulk-btn"
        >
          {bulkBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Zap className="w-3.5 h-3.5" />}
          {t.p3_ret_bulk_cta || `Odpovedať všetkým otvoreným (${tot.open})`}
        </button>
      )}

      {Object.keys(cats).length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {Object.entries(cats).map(([c, n]) => (
            <span key={c} className="text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-800 px-2 py-0.5 rounded">
              {c}: {n}
            </span>
          ))}
        </div>
      )}
      {(snap.recent || []).slice(0, 3).map((r) => {
        const isEditing = editingId === r.return_id;
        const isReplied = !!r.replied_at;
        const hasCustomer = r.customer_email && r.suggested_reply;
        return (
          <div key={r.return_id} className="mt-2 p-2 border border-gray-100 rounded text-[11px]" data-testid={`p3-return-${r.return_id}`}>
            <div className="flex items-center justify-between">
              <span className="font-bold text-gray-700">{r.category}</span>
              <span className="text-gray-400">{r.order_id || "—"}</span>
            </div>
            <p className="text-gray-600 line-clamp-1">{r.description}</p>
            {isEditing ? (
              <div className="mt-2 space-y-1">
                <textarea value={draft}
                          onChange={(e) => setDraft(e.target.value)}
                          rows={3}
                          className="w-full px-2 py-1 border border-gray-200 rounded text-[11px] font-mono"
                          data-testid={`p3-ret-textarea-${r.return_id}`}/>
                <div className="flex gap-1">
                  <button onClick={() => send(r, draft)}
                          disabled={sendingId === r.return_id || !draft.trim()}
                          className="bg-amber-600 hover:bg-amber-700 text-white text-[10px] font-bold px-2 py-1 rounded disabled:opacity-60"
                          data-testid={`p3-ret-send-custom-${r.return_id}`}>
                    {sendingId === r.return_id ? "..." : (t.p3_ret_send || "Odoslať")}
                  </button>
                  <button onClick={() => { setEditingId(null); setDraft(""); }}
                          className="bg-gray-100 hover:bg-gray-200 text-gray-700 text-[10px] font-bold px-2 py-1 rounded">
                    {t.p3_ret_cancel || "Zrušiť"}
                  </button>
                </div>
              </div>
            ) : (
              hasCustomer && !isReplied && (
                <div className="mt-2 flex gap-1 flex-wrap items-center">
                  <button onClick={() => send(r)}
                          disabled={sendingId === r.return_id}
                          className="bg-amber-600 hover:bg-amber-700 text-white text-[10px] font-bold px-2 py-1 rounded disabled:opacity-60 flex items-center gap-1"
                          data-testid={`p3-ret-send-${r.return_id}`}>
                    {sendingId === r.return_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                    {t.p3_ret_send_reply || "Odoslať odpoveď"}
                  </button>
                  <button onClick={() => { setEditingId(r.return_id); setDraft(r.suggested_reply || ""); }}
                          className="bg-white border border-amber-200 hover:bg-amber-50 text-amber-700 text-[10px] font-bold px-2 py-1 rounded"
                          data-testid={`p3-ret-edit-${r.return_id}`}>
                    {t.p3_ret_edit || "Upraviť"}
                  </button>
                </div>
              )
            )}
            {isReplied && (
              <span className="text-[10px] text-emerald-600 mt-1 inline-block" data-testid={`p3-ret-replied-${r.return_id}`}>
                ✓ {t.p3_ret_replied || "Odpoveď odoslaná"} {r.reply_sent_to && `→ ${r.reply_sent_to}`}
              </span>
            )}
            {statusMsg?.id === r.return_id && (
              <p className={`text-[10px] mt-1 ${statusMsg.err ? "text-rose-600" : "text-emerald-600"}`} data-testid={`p3-ret-status-${r.return_id}`}>
                {statusMsg.text}
              </p>
            )}
          </div>
        );
      })}

      {bulkOpen && (
        <BulkReplyModal
          preview={bulkPreview}
          result={bulkResult}
          busy={bulkBusy}
          onConfirm={runBulk}
          onClose={closeBulk}
          t={t}
        />
      )}
    </div>
  );
}

function BulkReplyModal({ preview, result, busy, onConfirm, onClose, t }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
         data-testid="p3-ret-bulk-modal" onClick={onClose}>
      <div className="bg-white rounded-xl max-w-lg w-full p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        {!result ? (
          <>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-orange-500 rounded-lg flex items-center justify-center">
                <Zap className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-black text-gray-900 font-['Chivo']">
                  {t.p3_ret_bulk_title || "Odpovedať všetkým otvoreným"}
                </h3>
                <p className="text-xs text-gray-500">
                  {preview?.count || 0} {t.p3_ret_bulk_count_suffix || "otvorených vrátení sa odošle s AI-drafted odpoveďou"}
                </p>
              </div>
            </div>

            {(preview?.preview || []).length > 0 && (
              <div className="bg-gray-50 rounded-lg p-3 mb-4 space-y-2">
                <p className="text-[10px] uppercase tracking-wider font-bold text-gray-500">
                  {t.p3_ret_bulk_preview || "Náhľad prvých 2 odpovedí"}
                </p>
                {preview.preview.map((p, idx) => (
                  <div key={p.return_id} className="text-xs border-l-2 border-amber-400 pl-2" data-testid={`p3-ret-bulk-preview-${idx}`}>
                    <p className="font-bold text-gray-700">
                      {p.customer_email} <span className="text-gray-400 font-normal">· {p.category} · {p.order_id || "—"}</span>
                    </p>
                    <p className="text-gray-600 mt-0.5 italic">"{p.snippet}"</p>
                  </div>
                ))}
                {(preview.count || 0) > 2 && (
                  <p className="text-[10px] text-gray-400 mt-1">
                    + {preview.count - 2} {t.p3_ret_bulk_more || "ďalších s rovnakým vzorom"}
                  </p>
                )}
              </div>
            )}

            <div className="flex gap-2 mt-5">
              <button
                onClick={onConfirm}
                disabled={busy || !preview || preview.count === 0}
                className="flex-1 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white text-sm font-bold py-2.5 rounded-lg disabled:opacity-60 flex items-center justify-center gap-2"
                data-testid="p3-ret-bulk-confirm"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {busy ? (t.p3_ret_bulk_sending || "Odosielam…") : (t.p3_ret_bulk_confirm || `Odoslať ${preview?.count || 0} odpovedí`)}
              </button>
              <button
                onClick={onClose}
                disabled={busy}
                className="bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-bold px-4 py-2.5 rounded-lg disabled:opacity-60"
                data-testid="p3-ret-bulk-cancel"
              >
                {t.p3_ret_bulk_cancel || "Zrušiť"}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <h3 className="text-lg font-black text-gray-900 font-['Chivo']">
                  {t.p3_ret_bulk_done || "AI agent dokončil úlohu"}
                </h3>
                <p className="text-xs text-gray-500">
                  {result.sent}/{result.total} {t.p3_ret_bulk_sent_suffix || "odpovedí odoslaných"}
                  {result.elapsed_seconds != null && (
                    <> · {result.elapsed_seconds}s</>
                  )}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-2 mb-4">
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-center">
                <p className="text-2xl font-black text-emerald-700">{result.sent}</p>
                <p className="text-[10px] uppercase tracking-wider text-emerald-700 font-bold">{t.p3_ret_bulk_sent || "Odoslaných"}</p>
              </div>
              <div className="bg-rose-50 border border-rose-200 rounded-lg p-3 text-center">
                <p className="text-2xl font-black text-rose-700">{result.failed}</p>
                <p className="text-[10px] uppercase tracking-wider text-rose-700 font-bold">{t.p3_ret_bulk_failed || "Zlyhalo"}</p>
              </div>
              <div className="bg-violet-50 border border-violet-200 rounded-lg p-3 text-center">
                <p className="text-2xl font-black text-violet-700">{result.elapsed_seconds || "—"}<span className="text-sm">s</span></p>
                <p className="text-[10px] uppercase tracking-wider text-violet-700 font-bold">{t.p3_ret_bulk_time || "Doba"}</p>
              </div>
            </div>

            {result.autonomy_event_id && result.sent > 0 && (
              <p className="text-[11px] text-gray-500 bg-gray-50 rounded p-2 mb-3" data-testid="p3-ret-bulk-autonomy">
                🤖 {t.p3_ret_bulk_autonomy || "Agent autonomy event:"} <code className="text-[10px]">{result.autonomy_event_id}</code>
              </p>
            )}

            <button
              onClick={onClose}
              className="w-full bg-gray-900 hover:bg-gray-800 text-white text-sm font-bold py-2.5 rounded-lg"
              data-testid="p3-ret-bulk-close"
            >
              {t.p3_ret_bulk_close || "Zavrieť"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function ShippingBody({ snap, t }) {
  const tot = snap.totals || {};
  return (
    <div className="space-y-1" data-testid="p3-shipping-body">
      <StatRow label={t.p3_in_transit || "In transit"} value={tot.in_transit || 0} />
      <StatRow label={t.p3_delivered_7d || "Doručené 7d"} value={tot.delivered_last_7d || 0} />
      <StatRow label={t.p3_stuck || "Uviaznuté"} value={tot.stuck || 0} trend={tot.stuck > 0 ? "down" : null} />
      {(snap.carriers_30d || []).length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {snap.carriers_30d.map((c) => (
            <span key={c.name} className="text-[10px] font-bold uppercase tracking-wider bg-blue-100 text-blue-800 px-2 py-0.5 rounded">
              {c.name}: {c.count}
            </span>
          ))}
        </div>
      )}
      {snap.hint && <p className="text-[10px] text-amber-700 bg-amber-50 px-2 py-1 rounded mt-2">{snap.hint}</p>}
    </div>
  );
}

function TaxBody({ snap, t }) {
  const tot = snap.totals || {};
  return (
    <div className="space-y-1" data-testid="p3-tax-body">
      <StatRow label={t.p3_vat_due || "DPH na úhradu"} value={`€${tot.vat_due_eur || 0}`} />
      <StatRow label={t.p3_countries || "Krajiny"} value={tot.countries || 0} />
      <StatRow label={t.p3_next_deadline || "Najbližšia OSS deadline"} value={tot.next_oss_deadline || "—"} />
      {snap.period && (
        <p className="text-[10px] text-gray-500 mt-1 font-mono">
          {t.p3_period || "Obdobie"}: {snap.period.from} → {snap.period.to}
        </p>
      )}
      {(snap.breakdown || []).slice(0, 3).map((b) => (
        <div key={b.country} className="flex items-center justify-between mt-1 text-[11px]">
          <span className="flex items-center gap-1"><Globe2 className="w-3 h-3" />{b.country} ({b.vat_rate_pct}%)</span>
          <span className="font-bold">€{b.vat_due}</span>
        </div>
      ))}
      {snap.hint && <p className="text-[10px] text-amber-700 bg-amber-50 px-2 py-1 rounded mt-2">{snap.hint}</p>}
    </div>
  );
}

function ChargebackBody({ snap, t }) {
  const tot = snap.totals || {};
  return (
    <div className="space-y-1" data-testid="p3-chargeback-body">
      <StatRow label={t.p3_cb_open || "Otvorené"} value={tot.open || 0} trend={tot.open > 0 ? "down" : null} />
      <StatRow label={t.p3_cb_at_risk || "V ohrození €"} value={`€${tot.at_risk_eur || 0}`} />
      <StatRow label={t.p3_cb_won || "Vyhrané 30d"} value={tot.won_30d || 0} trend="up" />
      <StatRow label={t.p3_cb_winrate || "Win rate"} value={`${tot.win_rate_pct || 0}%`} />
      {snap.hint && <p className="text-[10px] text-amber-700 bg-amber-50 px-2 py-1 rounded mt-2">{snap.hint}</p>}
    </div>
  );
}

function WebhookPanel({ agent, email, snap, color, t, onChange }) {
  const existing = snap?.agent?.slack_webhook_url || "";
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState(existing);
  const [status, setStatus] = useState(null); // saving | saved | testing | test-ok | test-failed | error:msg

  // Re-sync if parent reloads
  useEffect(() => { setUrl(existing); /* eslint-disable-next-line */ }, [existing]);

  const save = async () => {
    setStatus("saving");
    try {
      const r = await axios.post(`${API}/phase3/${agent}/configure-webhook`, {
        email, slack_webhook_url: url,
      });
      setStatus(r.data?.webhook_configured ? "saved" : "saved");
      onChange && (await onChange());
      setTimeout(() => setStatus(null), 2000);
    } catch (e) {
      setStatus("error:" + (e?.response?.data?.detail || "save_failed"));
    }
  };

  const test = async () => {
    setStatus("testing");
    try {
      const r = await axios.post(`${API}/phase3/test-webhook-preview/${agent}`, {
        email, slack_webhook_url: url,
      });
      setStatus(r.data?.ok ? "test-ok" : "test-failed");
      setTimeout(() => setStatus(null), 4000);
    } catch (e) {
      setStatus("error:" + (e?.response?.data?.detail || "test_failed"));
    }
  };

  const isConfigured = !!existing;

  return (
    <div className="mt-3 border-t border-gray-100 pt-3" data-testid={`p3-webhook-${agent}`}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between gap-2 text-xs font-bold text-gray-700 hover:text-gray-900"
        data-testid={`p3-webhook-toggle-${agent}`}>
        <span className="flex items-center gap-2">
          <Bell className={`w-3.5 h-3.5 ${isConfigured ? `text-${color}-600` : "text-gray-400"}`} />
          {t.p3_webhook || "Slack / Discord alerts"}
          {isConfigured && (
            <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 flex items-center gap-1">
              <CheckCircle className="w-2.5 h-2.5" /> ON
            </span>
          )}
        </span>
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="mt-3 space-y-2">
          <input
            type="text"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://hooks.slack.com/services/... or https://discord.com/api/webhooks/..."
            className="w-full px-2 py-1.5 border border-gray-300 rounded text-[11px] font-mono"
            data-testid={`p3-webhook-input-${agent}`}
          />
          <div className="flex gap-1.5 items-center flex-wrap">
            <button onClick={save}
                    disabled={status === "saving"}
                    className={`bg-${color}-600 hover:bg-${color}-700 text-white text-[11px] font-bold px-3 py-1.5 rounded disabled:opacity-60`}
                    data-testid={`p3-webhook-save-${agent}`}>
              {status === "saving" ? "..." : (t.p3_webhook_save || "Uložiť")}
            </button>
            <button onClick={test}
                    disabled={!url || status === "testing"}
                    className="bg-gray-100 hover:bg-gray-200 text-gray-700 text-[11px] font-bold px-3 py-1.5 rounded disabled:opacity-60 flex items-center gap-1"
                    data-testid={`p3-webhook-test-${agent}`}>
              <Send className="w-3 h-3" />
              {status === "testing" ? "..." : (t.p3_webhook_test || "Skúšobný alert")}
            </button>
            {status === "saved" && <span className="text-[10px] text-emerald-600">✓ {t.p3_webhook_saved || "Uložené"}</span>}
            {status === "test-ok" && <span className="text-[10px] text-emerald-600">✓ {t.p3_webhook_test_ok || "Alert odoslaný"}</span>}
            {status === "test-failed" && <span className="text-[10px] text-rose-600">✗ {t.p3_webhook_test_failed || "Webhook nedoručený"}</span>}
            {(status || "").startsWith("error:") && (
              <span className="text-[10px] text-rose-600">✗ {status.slice(6)}</span>
            )}
          </div>
          <p className="text-[10px] text-gray-400">
            💡 {t.p3_webhook_hint || "Skúšobný alert posiela reálnu ukážku notifikácie do tvojho Slack/Discord kanálu."}
          </p>
        </div>
      )}
    </div>
  );
}


function DailyBriefPanel({ email, t }) {
  const [enabled, setEnabled] = useState(null); // null = loading
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState(null);

  useEffect(() => {
    if (!email) return;
    axios.get(`${API}/phase3/daily-brief/preferences`, { params: { email } })
      .then((r) => setEnabled(!!r.data?.enabled))
      .catch(() => setEnabled(false));
  }, [email]);

  const toggle = async () => {
    setSaving(true);
    try {
      const r = await axios.patch(`${API}/phase3/daily-brief/preferences`, {
        email, enabled: !enabled,
      });
      setEnabled(!!r.data?.enabled);
      setStatus(r.data?.enabled ? "enabled" : "disabled");
      setTimeout(() => setStatus(null), 2500);
    } catch (e) {
      setStatus("error");
    } finally {
      setSaving(false);
    }
  };

  const sendNow = async () => {
    setSending(true);
    try {
      const r = await axios.post(`${API}/phase3/daily-brief/send-now`, { email });
      setStatus(r.data?.ok ? `sent:${r.data.agents_included}` : `failed:${r.data?.reason || "unknown"}`);
      setTimeout(() => setStatus(null), 5000);
    } catch (e) {
      setStatus(`failed:${e?.response?.data?.detail || "error"}`);
    } finally {
      setSending(false);
    }
  };

  if (enabled === null) return null;

  return (
    <div className="bg-gradient-to-r from-indigo-50 to-violet-50 border border-indigo-200 rounded-lg p-4 flex items-center gap-3 flex-wrap" data-testid="p3-daily-brief-panel">
      <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center shadow-sm">
        <Mail className="w-5 h-5 text-indigo-600" />
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="font-bold text-gray-900 text-sm flex items-center gap-2">
          {t.p3_brief_title || "Ranný Brief"}
          {enabled && (
            <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-emerald-100 text-emerald-700">
              {t.p3_brief_on || "Zapnuté"}
            </span>
          )}
        </h3>
        <p className="text-xs text-gray-600 mt-0.5">
          {t.p3_brief_subtitle || "Každé ráno o 09:00 CET pošleme ti HTML súhrn všetkých 5 agentov na email."}
        </p>
      </div>
      <div className="flex items-center gap-2">
        <button
          onClick={toggle}
          disabled={saving}
          className={`text-xs font-bold px-3 py-2 rounded disabled:opacity-60 ${
            enabled
              ? "bg-gray-200 hover:bg-gray-300 text-gray-700"
              : "bg-indigo-600 hover:bg-indigo-700 text-white"
          }`}
          data-testid="p3-brief-toggle"
        >
          {saving ? "..." : enabled ? (t.p3_brief_disable || "Vypnúť") : (t.p3_brief_enable || "Zapnúť")}
        </button>
        <button
          onClick={sendNow}
          disabled={sending}
          className="text-xs font-bold px-3 py-2 rounded bg-white border border-indigo-200 hover:bg-indigo-50 text-indigo-700 disabled:opacity-60 flex items-center gap-1"
          data-testid="p3-brief-send-now"
        >
          {sending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
          {sending ? "..." : (t.p3_brief_send_now || "Poslať teraz")}
        </button>
      </div>
      {status && (
        <div className="w-full text-xs mt-1" data-testid="p3-brief-status">
          {status === "enabled" && <span className="text-emerald-700">✓ {t.p3_brief_enabled_msg || "Daily Brief zapnutý — prvý email zajtra ráno."}</span>}
          {status === "disabled" && <span className="text-gray-600">{t.p3_brief_disabled_msg || "Daily Brief vypnutý."}</span>}
          {status.startsWith("sent:") && <span className="text-emerald-700">✓ {t.p3_brief_sent_msg || "Email odoslaný"} ({status.split(":")[1]} {t.p3_brief_agents_in || "agentov v briefe"})</span>}
          {status.startsWith("failed:") && <span className="text-rose-600">✗ {status.split(":")[1]}</span>}
          {status === "error" && <span className="text-rose-600">✗ {t.p3_brief_save_error || "Nepodarilo sa uložiť"}</span>}
        </div>
      )}
    </div>
  );
}

